import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * AI Drone composer — full-screen modal.
 *
 * Realtor uploads:
 *  - a ground-level wide photo of the front of the property
 *  - a Google Earth screenshot framed on the property
 *
 * Backend composites a high-oblique aerial bridge frame, which becomes
 * a new image record on the listing (drop into a reel, or use as the
 * source for a Magnific drone pull-back clip).
 */
export default function AIDronePicker({ listingId, onClose, onDone }) {
  const [ground, setGround] = useState(null);
  const [earth, setEarth] = useState(null);
  // Default: produce a real DRONE VIDEO (composite → Seedance pull-back).
  // Toggle off if the realtor just wants the still composite for use as
  // an image inside an existing reel.
  const [asVideo, setAsVideo] = useState(true);
  // descent = aerial composite → ground photo, two-frame Seedance reveal.
  // pullback = single-frame Seedance, camera rises and widens.
  const [videoMode, setVideoMode] = useState("descent");
  const [busy, setBusy] = useState(false);
  // Auto-append the finished clip to an existing reel ("" = don't append).
  const [appendReelId, setAppendReelId] = useState("");
  const [reels, setReels] = useState([]);
  const groundRef = useRef(null);
  const earthRef = useRef(null);

  // Load this listing's existing reels so the realtor can pick one to
  // auto-append the finished drone clip to.  Only reels with at least
  // one playable shot are useful targets.
  useEffect(() => {
    if (!listingId) {
      setReels([]);
      return;
    }
    api.get(`/reels?listing_id=${listingId}`)
      .then(({ data }) => {
        const usable = (data || []).filter(
          (r) => !r.is_deleted && (r.shot_job_ids || []).length > 0,
        );
        setReels(usable);
      })
      .catch(() => setReels([]));
  }, [listingId]);

  const pickImage = (setter) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setter({ file: f, preview: URL.createObjectURL(f), name: f.name });
  };

  const go = async () => {
    if (!ground || !earth) {
      toast.error("Add both a ground photo and a Google Earth screenshot");
      return;
    }
    setBusy(true);
    const tId = toast.loading(
      asVideo ? "Compositing + animating drone shot…" : "Compositing aerial frame…",
      {
        description: asVideo
          ? "~6-10s to bridge views, then 30-60s of Seedance motion."
          : "Typically 6-10s. Nano Banana is bridging the two views.",
        duration: Infinity,
      }
    );
    try {
      const fd = new FormData();
      if (listingId) fd.append("listing_id", listingId);
      fd.append("ground_image", ground.file);
      fd.append("earth_image", earth.file);
      if (asVideo) {
        fd.append("duration", "10");
        fd.append("mode", videoMode);
        if (appendReelId) fd.append("append_to_reel_id", appendReelId);
      }
      const endpoint = asVideo ? "/ai-drone/video" : "/ai-drone/oblique-frame";
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      toast.success(
        asVideo ? "Drone video job queued." : "Aerial frame added.",
        {
          id: tId,
          description: asVideo
            ? (appendReelId
                ? `Will auto-append to '${reels.find((r) => r.id === appendReelId)?.title || "selected reel"}' when ready (~60s).`
                : `Check the Library in 30-60s — it'll appear as 'AI Drone · ${videoMode === "descent" ? "Descent" : "Pullback"}'.`)
            : "Drop it into a reel or run a drone pull-back motion on it.",
          duration: 8000,
        }
      );
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drone composite failed", {
        id: tId,
        duration: 8000,
      });
    } finally {
      setBusy(false);
    }
  };

  const Pad = ({ slot, label, hint, refEl, value, setValue }) => (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </div>
      <button
        type="button"
        onClick={() => refEl.current?.click()}
        className="w-full aspect-video bg-black/40 border-2 border-dashed border-white/10 rounded-lg overflow-hidden hover:border-amber-500/40 transition-colors flex items-center justify-center"
        data-testid={`ai-drone-${slot}-pick`}
      >
        {value ? (
          <img
            src={value.preview}
            alt={label}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center px-4">
            <Icons.UploadCloud size={22} className="text-zinc-600 mx-auto mb-2" />
            <div className="text-[11px] text-zinc-400">Click to upload</div>
            <div className="text-[10px] text-zinc-600 mt-1">{hint}</div>
          </div>
        )}
      </button>
      <input
        type="file"
        accept="image/*"
        ref={refEl}
        className="hidden"
        onChange={pickImage(setValue)}
      />
      {value && (
        <button
          onClick={() => setValue(null)}
          className="text-[10px] text-zinc-500 hover:text-white"
        >
          Remove
        </button>
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="ai-drone-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <Icons.Plane size={15} className="text-amber-400" />
            <div>
              <div className="text-[13px] text-white font-medium">
                AI Drone shot
              </div>
              <div className="text-[10px] text-zinc-500">
                Ground photo + Google Earth screenshot → synthetic aerial.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="ai-drone-close"
          >
            <Icons.X size={15} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Pad
              slot="ground"
              label="Ground photo"
              hint="Wide front-of-house photo. JPEG or PNG."
              refEl={groundRef}
              value={ground}
              setValue={setGround}
            />
            <Pad
              slot="earth"
              label="Google Earth screenshot"
              hint="Top-down view framed on the property + neighbours."
              refEl={earthRef}
              value={earth}
              setValue={setEarth}
            />
          </div>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-[11px] text-amber-200/80 leading-relaxed">
            <div className="flex items-start gap-2">
              <Icons.Info size={12} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-amber-300 font-medium mb-0.5">How to get the Earth screenshot</div>
                Open Google Earth → search the address → tilt the camera so it's
                roughly top-down → take a screenshot that includes the house
                and 2-3 neighbouring lots. The wider you frame, the more
                authentic the pull-back feels.
              </div>
            </div>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-black/40 cursor-pointer hover:border-white/30 transition-colors">
            <input
              type="checkbox"
              checked={asVideo}
              onChange={(e) => setAsVideo(e.target.checked)}
              className="mt-1 accent-amber-500"
              data-testid="ai-drone-as-video"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-white font-medium flex items-center gap-1.5">
                <Icons.Video size={12} className="text-amber-400" />
                Animate as drone video
                <span className="text-[9px] tracking-[0.2em] uppercase text-amber-500/70 ml-1 px-1.5 py-0.5 rounded border border-amber-500/30">
                  Default
                </span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                Composites the bridge frame, then auto-animates it via Seedance
                drone pull-back. ~60s total. Uncheck to just add a still image
                to the listing.
              </div>
            </div>
          </label>

          {asVideo && (
            <div className="space-y-2 pl-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Camera move
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    videoMode === "descent"
                      ? "border-amber-500/60 bg-amber-500/10"
                      : "border-white/10 bg-black/40 hover:border-white/30"
                  }`}
                  data-testid="ai-drone-mode-descent"
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={videoMode === "descent"}
                    onChange={() => setVideoMode("descent")}
                  />
                  <div className="text-[12px] text-white font-medium flex items-center gap-1.5">
                    <Icons.ArrowDownToLine size={12} className="text-amber-400" />
                    Descent (sky → front door)
                    <span className="text-[9px] tracking-[0.2em] uppercase text-amber-500/70 ml-1 px-1.5 py-0.5 rounded border border-amber-500/30">
                      New
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                    Two-frame Seedance: starts aerial, lands on your ground
                    photo. Matches the competitor's flagship "drone reveal".
                  </div>
                </label>
                <label
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    videoMode === "pullback"
                      ? "border-amber-500/60 bg-amber-500/10"
                      : "border-white/10 bg-black/40 hover:border-white/30"
                  }`}
                  data-testid="ai-drone-mode-pullback"
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={videoMode === "pullback"}
                    onChange={() => setVideoMode("pullback")}
                  />
                  <div className="text-[12px] text-white font-medium flex items-center gap-1.5">
                    <Icons.ArrowUpFromLine size={12} className="text-amber-400" />
                    Pullback (rises + widens)
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                    Single-frame: camera lifts and reveals the lot. Better for
                    properties with big land.
                  </div>
                </label>
              </div>
            </div>
          )}
          {asVideo && reels.length > 0 && (
            <div className="space-y-2 pl-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5">
                <Icons.Layers size={11} className="text-amber-400" />
                Auto-add to existing reel <span className="text-[9px] text-zinc-600">(optional)</span>
              </div>
              <select
                value={appendReelId}
                onChange={(e) => setAppendReelId(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-[12px] text-white focus:outline-none focus:border-amber-500/40"
                data-testid="ai-drone-append-reel"
              >
                <option value="">— Don't auto-add, just save to Library —</option>
                {reels.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title || "Untitled reel"} · {(r.shot_job_ids || []).length} clip{(r.shot_job_ids || []).length === 1 ? "" : "s"}
                    {r.status === "done" ? " · ready" : ` · ${r.status || "—"}`}
                  </option>
                ))}
              </select>
              {appendReelId && (
                <div className="text-[10px] text-amber-300/80 leading-snug">
                  When Seedance finishes, this clip will be appended to the end of the reel and the final video will be re-stitched automatically.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-end gap-2 bg-black/30">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[11px] text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={go}
            disabled={busy || !ground || !earth}
            className="px-3.5 py-1.5 rounded-md text-[11px] bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="ai-drone-go"
          >
            {busy ? (
              <>
                <Icons.Loader2 size={11} className="animate-spin" />
                {asVideo ? "Generating video…" : "Compositing…"}
              </>
            ) : (
              <>
                <Icons.Sparkles size={11} />
                {asVideo ? "Generate drone video" : "Generate aerial frame"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
